// Right-hand rail: library picks for this person, and what was just added.
import { Link } from "react-router-dom";
import { AppWindow, BookOpen, Bot, FileText, Layers, Lightbulb, Sparkles, type LucideIcon } from "lucide-react";
import type { CatalogItem, LibraryItem, ListKind } from "../../types/navigator";
import type { RecentItem } from "../../api/graph";
import { timeAgo } from "./format";

export function assetIcon(type?: string): LucideIcon {
  const t = (type ?? "").toLowerCase();
  if (t.includes("prompt")) return Sparkles;
  if (t.includes("agent") || t.includes("bot")) return Bot;
  if (t.includes("template") || t.includes("doc")) return FileText;
  if (t.includes("app")) return AppWindow;
  return BookOpen;
}

const KIND: Record<ListKind, { label: string; icon: LucideIcon; tone: string }> = {
  library: { label: "Library", icon: BookOpen, tone: "library" },
  initiative: { label: "Initiative", icon: Lightbulb, tone: "initiative" },
  platform: { label: "Platform", icon: Layers, tone: "platform" },
};

function ItemRow({
  icon: Icon,
  tone,
  title,
  meta,
  onClick,
}: {
  icon: LucideIcon;
  tone: string;
  title: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button type="button" className="db-item" onClick={onClick}>
        <span className={`db-item-icon db-kind--${tone}`} aria-hidden>
          <Icon size={16} />
        </span>
        <span className="db-item-body">
          <span className="db-item-title">{title}</span>
          <span className="db-item-meta">{meta}</span>
        </span>
      </button>
    </li>
  );
}

function SkeletonRows() {
  return (
    <div className="db-skel-rows db-skel-rows--small" aria-busy="true">
      <span />
      <span />
      <span />
    </div>
  );
}

export function PickedForYou({
  items,
  loading,
  personalized,
  matchedOn,
  onOpen,
}: {
  items: LibraryItem[];
  loading: boolean;
  personalized: boolean;
  matchedOn: string | null;
  onOpen: (item: CatalogItem) => void;
}) {
  return (
    <section className="db-card db-rail-card" aria-labelledby="db-picks-title">
      <div className="db-card-head">
        <h2 id="db-picks-title" className="db-rail-title">
          Picked for you
        </h2>
        <Link className="db-link db-link--small" to="/library">
          See all
        </Link>
      </div>
      <p className="db-rail-sub">
        {personalized && matchedOn ? `Matched to your role: ${matchedOn}` : "Popular tools in the AI Library"}
      </p>
      {loading && !items.length ? (
        <SkeletonRows />
      ) : items.length ? (
        <ul className="db-items">
          {items.slice(0, 4).map((it) => (
            <ItemRow
              key={it.id}
              icon={assetIcon(it.assetType)}
              tone="library"
              title={it.title}
              meta={[it.assetType, it.department].filter(Boolean).join(" · ")}
              onClick={() => onOpen(it)}
            />
          ))}
        </ul>
      ) : (
        <p className="db-empty-text">Nothing to suggest yet.</p>
      )}
    </section>
  );
}

export function RecentlyAdded({
  items,
  loading,
  onOpen,
}: {
  items: RecentItem[];
  loading: boolean;
  onOpen: (item: CatalogItem) => void;
}) {
  return (
    <section className="db-card db-rail-card" aria-labelledby="db-recent-title">
      <div className="db-card-head">
        <h2 id="db-recent-title" className="db-rail-title">
          Recently updated
        </h2>
      </div>
      <p className="db-rail-sub">Across the library, initiatives and platforms</p>
      {loading && !items.length ? (
        <SkeletonRows />
      ) : items.length ? (
        <ul className="db-items">
          {items.slice(0, 5).map(({ kind, item }) => {
            const k = KIND[kind];
            return (
              <ItemRow
                key={`${kind}-${item.id}`}
                icon={k.icon}
                tone={k.tone}
                title={item.title}
                meta={[k.label, timeAgo(item.modified || item.created)].filter(Boolean).join(" · ")}
                onClick={() => onOpen(item)}
              />
            );
          })}
        </ul>
      ) : (
        <p className="db-empty-text">Nothing new yet.</p>
      )}
    </section>
  );
}
